/**
 * Tenant Management Component
 * Allows super admin to create, view, edit, and delete tenants
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Building2,
    Plus,
    Trash2,
    Eye,
    EyeOff,
    RefreshCw,
    AlertCircle,
    Loader2,
    CheckCircle,
    XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { cn } from '../../lib/utils';
import superAdminService from '../../services/superAdminService';
import { logger } from '../../utils/logger';

function TenantManagement() {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [selectedTenants, setSelectedTenants] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        username: '',
        password: '',
        company_name: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchTenants = useCallback(async () => {
        try {
            setLoading(true);
            const data = await superAdminService.getTenants();
            setTenants(data);
            setError(null);
        } catch (err) {
            logger.error('Error fetching tenants:', err);
            setError('Failed to load tenants');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTenants();
    }, [fetchTenants]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreateTenant = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (!formData.name || !formData.email || !formData.username || !formData.password) {
            setError('Please fill in all required fields');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setIsSubmitting(true);

        try {
            await superAdminService.createTenant(formData);

            setSuccess('Tenant created successfully!');
            setFormData({
                name: '',
                email: '',
                username: '',
                password: '',
                company_name: ''
            });
            setShowCreateForm(false);
            fetchTenants();
        } catch (err) {
            const errorMessage = err.response?.data?.detail?.message
                || err.response?.data?.detail
                || 'Failed to create tenant';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleStatus = async (tenantId) => {
        try {
            await superAdminService.toggleTenantStatus(tenantId);
            setSuccess('Tenant status updated');
            fetchTenants();
        } catch (err) {
            setError('Failed to update tenant status');
        }
    };

    const handleDelete = async (tenantId) => {
        if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
            return;
        }

        try {
            await superAdminService.deleteTenant(tenantId);
            setSuccess('Tenant deleted successfully');
            fetchTenants();
        } catch (err) {
            setError('Failed to delete tenant');
        }
    };

    // Bulk actions handlers
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedTenants(tenants.map(t => t.id));
        } else {
            setSelectedTenants([]);
        }
    };

    const handleSelectTenant = (tenantId, checked) => {
        if (checked) {
            setSelectedTenants([...selectedTenants, tenantId]);
        } else {
            setSelectedTenants(selectedTenants.filter(id => id !== tenantId));
        }
    };

    const handleBulkActivate = async () => {
        if (selectedTenants.length === 0) return;
        try {
            setIsSubmitting(true);
            for (const id of selectedTenants) {
                await superAdminService.toggleTenantStatus(id);
            }
            setSuccess(`${selectedTenants.length} tenants activated`);
            setSelectedTenants([]);
            fetchTenants();
        } catch (err) {
            setError('Failed to activate tenants');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedTenants.length === 0) return;
        if (!window.confirm(`Delete ${selectedTenants.length} tenants? This cannot be undone.`)) return;

        try {
            setIsSubmitting(true);
            for (const id of selectedTenants) {
                await superAdminService.deleteTenant(id);
            }
            setSuccess(`${selectedTenants.length} tenants deleted`);
            setSelectedTenants([]);
            fetchTenants();
        } catch (err) {
            setError('Failed to delete tenants');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Tenant Management</h2>
                    <p className="text-gray-600">Create and manage all tenants</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={fetchTenants}
                    >
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="bg-purple-600 hover:bg-purple-700"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Tenant
                    </Button>
                </div>
            </div>

            {/* Alerts */}
            {error && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            {success && (
                <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">{success}</AlertDescription>
                </Alert>
            )}

            {/* Create Tenant Form */}
            {showCreateForm && (
                <Card className="border-purple-200">
                    <CardHeader>
                        <CardTitle>Create New Tenant</CardTitle>
                        <CardDescription>
                            Enter tenant admin credentials
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleCreateTenant} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name *</Label>
                                    <Input
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="John Doe"
                                        disabled={isSubmitting}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address *</Label>
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="john@company.com"
                                        disabled={isSubmitting}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="username">Username *</Label>
                                    <Input
                                        id="username"
                                        name="username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        placeholder="johndoe"
                                        disabled={isSubmitting}
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password *</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Min. 8 characters"
                                            disabled={isSubmitting}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4 text-gray-500" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-gray-500" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2 col-span-2">
                                    <Label htmlFor="company_name">Company Name (Optional)</Label>
                                    <Input
                                        id="company_name"
                                        name="company_name"
                                        value={formData.company_name}
                                        onChange={handleChange}
                                        placeholder="Acme Inc."
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowCreateForm(false)}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-purple-600 hover:bg-purple-700"
                                >
                                    {isSubmitting ? 'Creating...' : 'Create Tenant'}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Bulk Actions Bar */}
            {selectedTenants.length > 0 && (
                <div className="bg-gradient-to-r from-[var(--g6-secondary)] to-[var(--g6-secondary-dark)] rounded-xl p-4 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-semibold">{selectedTenants.length} tenant{selectedTenants.length > 1 ? 's' : ''} selected</span>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleBulkActivate}
                                disabled={isSubmitting}
                                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                            >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Activate All
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleBulkDelete}
                                disabled={isSubmitting}
                                className="bg-red-500/80 hover:bg-red-600 text-white border-none"
                            >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete All
                            </Button>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setSelectedTenants([])}
                                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                            >
                                Clear Selection
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tenants List */}
            <div className="space-y-4">
                {tenants.length === 0 ? (
                    <Card>
                        <CardContent className="text-center py-12">
                            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-gray-600">No tenants found</p>
                            <p className="text-sm text-gray-500 mt-2">
                                Create your first tenant to get started
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {/* Select All Header */}
                        <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                            <input
                                type="checkbox"
                                checked={selectedTenants.length === tenants.length}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-[var(--g6-primary)] focus:ring-[var(--g6-primary)]"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Select All ({tenants.length} tenants)
                            </span>
                        </div>

                        {/* Tenant Cards */}
                        {tenants.map((tenant) => (
                            <Card key={tenant.id} className={cn(
                                "hover:border-[var(--g6-secondary)] transition-colors",
                                selectedTenants.includes(tenant.id) && "border-[var(--g6-secondary)] bg-cyan-50/30"
                            )}>
                                <CardContent className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedTenants.includes(tenant.id)}
                                                onChange={(e) => handleSelectTenant(tenant.id, e.target.checked)}
                                                className="h-5 w-5 rounded border-gray-300 text-[var(--g6-primary)] focus:ring-[var(--g6-primary)]"
                                            />
                                            <div className={cn(
                                                "h-12 w-12 rounded-lg flex items-center justify-center",
                                                tenant.is_active ? "bg-green-100" : "bg-gray-100"
                                            )}>
                                                <Building2 className={cn(
                                                    "h-6 w-6",
                                                    tenant.is_active ? "text-green-600" : "text-gray-400"
                                                )} />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-lg">{tenant.name || 'Unknown'}</h3>
                                                <p className="text-sm text-gray-500">
                                                    {tenant.admin_email || 'No email'}
                                                </p>
                                                <div className="flex gap-4 mt-1">
                                                    <span className={cn(
                                                        "text-xs px-2 py-1 rounded-full",
                                                        tenant.is_active
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-gray-100 text-gray-700"
                                                    )}>
                                                        {tenant.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        Plan: {tenant.plan}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleToggleStatus(tenant.id)}
                                            >
                                                {tenant.is_active ? (
                                                    <><XCircle className="h-4 w-4 mr-1" /> Deactivate</>
                                                ) : (
                                                    <><CheckCircle className="h-4 w-4 mr-1" /> Activate</>
                                                )}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                onClick={() => handleDelete(tenant.id)}
                                                className="text-red-600 hover:bg-red-50"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

export { TenantManagement };
export default TenantManagement;
